import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { IGettingStartedProgressItem, IGettingStartedTemplate } from '../../services/getting-started.service'

export const GETTING_STARTED_PANEL_WIDTH_PX = 300
export const GETTING_STARTED_PANEL_GUTTER_PX = 0
export const GETTING_STARTED_LAYOUT_RESERVE_PX = GETTING_STARTED_PANEL_WIDTH_PX + GETTING_STARTED_PANEL_GUTTER_PX

export enum eGettingStartedStepStatus {
  Incomplete = 'incomplete',
  Finished = 'finished',
  Skipped = 'skipped',
}

export const gettingStartedStepKey = (keyModules: string, target: string): string => `${keyModules}::${target}`

export const nextGettingStartedStepStatus = (status: eGettingStartedStepStatus): eGettingStartedStepStatus => {
  if (status === eGettingStartedStepStatus.Incomplete) return eGettingStartedStepStatus.Finished
  if (status === eGettingStartedStepStatus.Finished) return eGettingStartedStepStatus.Skipped
  return eGettingStartedStepStatus.Incomplete
}

const toStepStatus = (status: string): eGettingStartedStepStatus => {
  if (status === eGettingStartedStepStatus.Finished) return eGettingStartedStepStatus.Finished
  if (status === eGettingStartedStepStatus.Skipped) return eGettingStartedStepStatus.Skipped
  return eGettingStartedStepStatus.Incomplete
}

interface GettingStartedState {
  data: IGettingStartedTemplate[]
  progress: IGettingStartedProgressItem[]
  panelOpen: boolean
  stepStatuses: Record<string, eGettingStartedStepStatus>
  run: boolean
}

const buildStepStatuses = (
  templates: IGettingStartedTemplate[],
  previous: Record<string, eGettingStartedStepStatus> = {}
): Record<string, eGettingStartedStepStatus> => {
  const next: Record<string, eGettingStartedStepStatus> = {}
  templates.forEach((template) => {
    template.steps.forEach((step) => {
      const key = gettingStartedStepKey(template.keyModules, step.target)
      next[key] = previous[key] ?? eGettingStartedStepStatus.Incomplete
    })
  })
  // Keep progress-only keys (e.g. nested subSteps) that are not template top-level steps
  Object.entries(previous).forEach(([key, status]) => {
    if (!(key in next)) {
      next[key] = status
    }
  })
  return next
}

const progressToStepStatuses = (progress: IGettingStartedProgressItem[]): Record<string, eGettingStartedStepStatus> => {
  const next: Record<string, eGettingStartedStepStatus> = {}
  progress.forEach((item) => {
    next[gettingStartedStepKey(item.keyModules, item.target)] = toStepStatus(item.status)
  })
  return next
}

const initialState: GettingStartedState = {
  data: [],
  progress: [],
  panelOpen: false,
  stepStatuses: {},
  run: false,
}

export const gettingStartedSlice = createSlice({
  name: 'gettingStartedReducer',
  initialState,
  reducers: {
    getTemplateUser: (state, action: PayloadAction<{ data: IGettingStartedTemplate[] }>) => {
      state.data = [...action.payload.data]
      state.stepStatuses = buildStepStatuses(action.payload.data, state.stepStatuses)
    },
    setGettingStartedProgress: (state, action: PayloadAction<{ progress: IGettingStartedProgressItem[] }>) => {
      state.progress = [...action.payload.progress]
      state.stepStatuses = buildStepStatuses(state.data, {
        ...state.stepStatuses,
        ...progressToStepStatuses(action.payload.progress),
      })
    },
    setGettingStartedPanelOpen: (state, action: PayloadAction<boolean>) => {
      state.panelOpen = action.payload
    },
    setGettingStartedStepStatus: (state, action: PayloadAction<{ key: string; status: eGettingStartedStepStatus }>) => {
      state.stepStatuses[action.payload.key] = action.payload.status
    },
    cycleGettingStartedStepStatus: (state, action: PayloadAction<{ key: string }>) => {
      const current = state.stepStatuses[action.payload.key] ?? eGettingStartedStepStatus.Incomplete
      state.stepStatuses[action.payload.key] = nextGettingStartedStepStatus(current)
    },
    completeGettingStartedStep: (state, action: PayloadAction<{ keyModules: string; target: string }>) => {
      const key = gettingStartedStepKey(action.payload.keyModules, action.payload.target)
      const current = state.stepStatuses[key] ?? eGettingStartedStepStatus.Incomplete
      if (current === eGettingStartedStepStatus.Finished || current === eGettingStartedStepStatus.Skipped) {
        return
      }

      state.stepStatuses[key] = eGettingStartedStepStatus.Finished

      const existingIndex = state.progress.findIndex(
        (item) => item.keyModules === action.payload.keyModules && item.target === action.payload.target
      )
      if (existingIndex >= 0) {
        state.progress[existingIndex] = {
          ...state.progress[existingIndex],
          status: eGettingStartedStepStatus.Finished,
        }
      } else {
        state.progress.push({
          keyModules: action.payload.keyModules,
          target: action.payload.target,
          status: eGettingStartedStepStatus.Finished,
        })
      }
    },
    resetGettingStartedProgress: (state) => {
      state.stepStatuses = buildStepStatuses(state.data)
      state.progress = []
    },
    setRunStatusGettingStarted: (state, action: PayloadAction<{ run: boolean }>) => {
      state.run = action.payload.run
    },
  },
})

export const {
  getTemplateUser,
  setGettingStartedProgress,
  setGettingStartedPanelOpen,
  setGettingStartedStepStatus,
  cycleGettingStartedStepStatus,
  completeGettingStartedStep,
  resetGettingStartedProgress,
  setRunStatusGettingStarted,
} = gettingStartedSlice.actions
export default gettingStartedSlice.reducer
export const selectGettingStarted = (state: { gettingStartedReducer: GettingStartedState }): GettingStartedState =>
  state.gettingStartedReducer
